import React, { useEffect, useId, useMemo, useState } from "react";
import theme from "../../../styles/theme";
import DropdownPrint from "../../defined/Report/DropdownPrint";

/* ============================================================================
   PRINTMENUSTANDARD.JSX
   ----------------------------------------------------------------------------
   COMPOSANT STANDARD D’IMPRESSION / EXPORT DOCUMENTAIRE
   ----------------------------------------------------------------------------
   OBJECTIF
   --------
   Fournir un menu de préparation documentaire robuste, réutilisable dans
   plusieurs pages et onglets Corelia, sans logique métier enfouie.

   CAS D'USAGE CIBLES
   ------------------
   - rapport de risques
   - scope report
   - export de listes d’assets
   - synthèse projet
   - futures impressions techniques / documentaires

   PRINCIPES D’ARCHITECTURE
   ------------------------
   1. Le composant ne génère pas lui-même le document.
      Il prépare un payload normalisé et appelle `onGenerate(payload)`.

   2. Les données d’entrée restent pilotées par la page appelante :
      - items
      - templates
      - defaults
      - preview éventuel

   3. Le composant est conçu pour rester stable en cas de :
      - changement d’onglet
      - changement de dataset
      - nouvelle instance dans la même page
      - enrichissements futurs

   PAYLOAD ÉMIS
   ------------
   {
     selectedIds,
     onlyActive,
     format,         // "pdf" | "docx"
     template,       // value | null
     orientation,    // "portrait" | "landscape"
     fontSize,       // "small" | "medium" | "large"
     includeToc,     // boolean
   }

   AXES D’ENRICHISSEMENT FUTURS
   ----------------------------
   - marges d’impression
   - entête/pied de page
   - couverture / page de garde
   - watermark
   - sections à inclure
   - templates avec aperçu image réel
   - paramètres par type de rapport
============================================================================ */

/* ============================================================================
   STYLES INTERNES
   ----------------------------------------------------------------------------
   Styles en inline pour préserver une portabilité complète du composant,
   tout en restant cohérent avec theme.js
============================================================================ */

const sectionTitleStyle = {
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 8,
  color: theme.colors.text.primary,
};

const sectionBlockStyle = {
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: `1px solid ${theme.colors.borders.default}`,
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 8,
  flexWrap: "wrap",
};

const labelStyle = {
  fontSize: 13,
  color: theme.colors.text.body,
};

const selectStyle = {
  flex: 1,
  height: 32,
  borderRadius: 4,
  border: `1px solid ${theme.colors.borders.default}`,
  padding: "4px 8px",
  fontSize: 13,
  boxSizing: "border-box",
  background: theme.colors.background.input,
  color: theme.colors.text.default,
};

const radioGroupStyle = {
  display: "flex",
  flexDirection: "row",
  gap: 16,
  flexWrap: "wrap",
};

const buttonPrimaryStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 14px",
  borderRadius: 4,
  border: "none",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
  background: theme.buttons.blue.background,
  color: theme.buttons.blue.color,
};

const buttonSecondaryStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 12px",
  borderRadius: 4,
  border: `1px solid ${theme.colors.borders.default}`,
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
  background: theme.colors.background.surface,
  color: theme.colors.text.primary,
};

const thumbBoxStyle = {
  width: 140,
  height: 88,
  borderRadius: 4,
  border: `1px solid ${theme.colors.borders.default}`,
  background: theme.colors.background.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  color: theme.colors.text.muted,
  overflow: "hidden",
  boxSizing: "border-box",
  padding: 8,
  textAlign: "center",
};

const helperTextStyle = {
  fontSize: 12,
  color: theme.colors.text.small,
  lineHeight: 1.35,
};

/* ============================================================================
   HELPERS
============================================================================ */

const DEFAULT_IS_ACTIVE = (item) => item?.active !== false;

const normalizeIds = (items, getItemId) =>
  items.map((item) => String(getItemId(item)));

const arraysEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const clampSelectedIdsToAvailable = (selectedIds, allowedIdsSet) => {
  return selectedIds.filter((id) => allowedIdsSet.has(id));
};

const resolveDefaultTemplate = (templates, defaultTemplate) => {
  if (
    defaultTemplate !== null &&
    defaultTemplate !== undefined &&
    templates.some((tpl) => String(tpl.value) === String(defaultTemplate))
  ) {
    return String(defaultTemplate);
  }

  return templates.length > 0 ? String(templates[0].value) : null;
};

/* ============================================================================
   COMPOSANT
============================================================================ */

const PrintMenuStandard = ({
  items = [],
  getItemId = (item) => item.id,
  getItemLabel = (item) => item.title || item.label || `#${item.id}`,
  isActive = DEFAULT_IS_ACTIVE,

  templates = [],
  defaultTemplate = null,

  initialFormat = "pdf",
  initialOrientation = "portrait",
  initialFontSize = "medium",
  initialIncludeToc = true,
  initialOnlyActive = false,
  initialSelectAll = true,

  onGenerate = () => {},

  /* ------------------------------------------------------------------------
     Preview extensible
     ------------------------------------------------------------------------
     renderTemplatePreview(templateValue, selectedTemplateObject) => ReactNode
     Si non fourni, fallback textuel.
  ------------------------------------------------------------------------ */
  renderTemplatePreview,
}) => {
  const instanceId = useId();

  /* ------------------------------------------------------------------------
     Options dérivées
  ------------------------------------------------------------------------ */
  const normalizedTemplates = useMemo(() => {
    return templates.map((tpl) => ({
      ...tpl,
      value: String(tpl.value),
    }));
  }, [templates]);

  const itemOptions = useMemo(() => {
    return items.map((item) => ({
      value: String(getItemId(item)),
      label: getItemLabel(item),
    }));
  }, [items, getItemId, getItemLabel]);

  const allIds = useMemo(() => {
    return normalizeIds(items, getItemId);
  }, [items, getItemId]);

  const activeIds = useMemo(() => {
    return items
      .filter((item) => isActive(item))
      .map((item) => String(getItemId(item)));
  }, [items, isActive, getItemId]);

  const allowedIds = useMemo(() => {
    return new Set(allIds);
  }, [allIds]);

  const currentSelectableIds = useMemo(() => {
    return initialOnlyActive ? activeIds : allIds;
  }, [initialOnlyActive, activeIds, allIds]);

  const resolvedInitialTemplate = useMemo(() => {
    return resolveDefaultTemplate(normalizedTemplates, defaultTemplate);
  }, [normalizedTemplates, defaultTemplate]);

  /* ------------------------------------------------------------------------
     États locaux
  ------------------------------------------------------------------------ */
  const [selectedIds, setSelectedIds] = useState(() =>
    initialSelectAll ? currentSelectableIds : []
  );

  const [selectAll, setSelectAll] = useState(initialSelectAll);
  const [onlyActive, setOnlyActive] = useState(initialOnlyActive);

  const [format, setFormat] = useState(initialFormat); // "pdf" | "docx"
  const [selectedTemplate, setSelectedTemplate] = useState(resolvedInitialTemplate);
  const [orientation, setOrientation] = useState(initialOrientation); // "portrait" | "landscape"
  const [fontSize, setFontSize] = useState(initialFontSize); // "small" | "medium" | "large"
  const [includeToc, setIncludeToc] = useState(initialIncludeToc);

  /* ------------------------------------------------------------------------
     Synchronisation état <-> items
     ------------------------------------------------------------------------
     Si le dataset change, on conserve la cohérence :
     - on purge les ids devenus invalides
     - si selectAll est actif, on resynchronise avec le périmètre courant
  ------------------------------------------------------------------------ */
  useEffect(() => {
    const baseIds = onlyActive ? activeIds : allIds;

    if (selectAll) {
      setSelectedIds(baseIds);
      return;
    }

    setSelectedIds((prev) => {
      const clamped = clampSelectedIdsToAvailable(prev, allowedIds);
      return arraysEqual(prev, clamped) ? prev : clamped;
    });
  }, [allIds, activeIds, onlyActive, selectAll, allowedIds]);

  /* ------------------------------------------------------------------------
     Synchronisation template
     ------------------------------------------------------------------------
     Si les templates changent, on conserve une valeur valide.
  ------------------------------------------------------------------------ */
  useEffect(() => {
    setSelectedTemplate((prev) => {
      const prevString = prev === null || prev === undefined ? null : String(prev);

      if (
        prevString &&
        normalizedTemplates.some((tpl) => tpl.value === prevString)
      ) {
        return prevString;
      }

      return resolveDefaultTemplate(normalizedTemplates, defaultTemplate);
    });
  }, [normalizedTemplates, defaultTemplate]);

  /* ------------------------------------------------------------------------
     Etat dérivé
  ------------------------------------------------------------------------ */
  const selectedTemplateObject = useMemo(() => {
    return normalizedTemplates.find((tpl) => tpl.value === selectedTemplate) || null;
  }, [normalizedTemplates, selectedTemplate]);

  const effectiveSelectableIds = onlyActive ? activeIds : allIds;

  const canGenerate = selectedIds.length > 0;

  /* ------------------------------------------------------------------------
     Handlers
  ------------------------------------------------------------------------ */
  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);

    if (checked) {
      setSelectedIds(effectiveSelectableIds);
    }
  };

  const handleOnlyActiveChange = (checked) => {
    setOnlyActive(checked);

    if (selectAll) {
      setSelectedIds(checked ? activeIds : allIds);
      return;
    }

    if (checked) {
      const activeIdSet = new Set(activeIds);
      setSelectedIds((prev) => prev.filter((id) => activeIdSet.has(id)));
    }
  };

  const handleSelectedValuesChange = (values) => {
    const nextValues = values.map(String);
    setSelectedIds(nextValues);
    setSelectAll(nextValues.length === effectiveSelectableIds.length);
  };

  const handleReset = () => {
    const resetSelectedIds = initialSelectAll
      ? (initialOnlyActive ? activeIds : allIds)
      : [];

    setSelectedIds(resetSelectedIds);
    setSelectAll(initialSelectAll);
    setOnlyActive(initialOnlyActive);
    setFormat(initialFormat);
    setSelectedTemplate(resolveDefaultTemplate(normalizedTemplates, defaultTemplate));
    setOrientation(initialOrientation);
    setFontSize(initialFontSize);
    setIncludeToc(initialIncludeToc);
  };

  const handleGenerate = () => {
    if (!canGenerate) return;

    onGenerate({
      selectedIds,
      onlyActive,
      format,
      template: selectedTemplate,
      orientation,
      fontSize,
      includeToc: format === "docx" ? includeToc : false,
    });
  };

  /* ------------------------------------------------------------------------
     Preview
  ------------------------------------------------------------------------ */
  const previewContent = renderTemplatePreview
    ? renderTemplatePreview(selectedTemplate, selectedTemplateObject)
    : selectedTemplate
    ? "Preview available"
    : "No template";

  /* ------------------------------------------------------------------------
     Rendu
  ------------------------------------------------------------------------ */
  return (
    <div style={{ width: "100%", boxSizing: "border-box" }}>
      {/* ============================================================
         SECTION 1 — Selection
      ============================================================ */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Selection</div>

        <div style={{ marginBottom: 8 }}>
          <DropdownPrint
            label="Items to include"
            options={itemOptions}
            selectedValues={selectedIds}
            onChange={handleSelectedValuesChange}
            width="100%"
            height={32}
          />
        </div>

        <div style={rowStyle}>
          <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
            />
            Select all
          </label>

          <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => handleOnlyActiveChange(e.target.checked)}
            />
            Only active items
          </label>
        </div>

        <div style={helperTextStyle}>
          Selected: {selectedIds.length} / Available: {effectiveSelectableIds.length}
        </div>
      </div>

      {/* ============================================================
         SECTION 2 — Format
      ============================================================ */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Document Format</div>

        <div style={radioGroupStyle}>
          <label style={labelStyle}>
            <input
              type="radio"
              name={`print-format-${instanceId}`}
              value="pdf"
              checked={format === "pdf"}
              onChange={() => setFormat("pdf")}
              style={{ marginRight: 6 }}
            />
            PDF
          </label>

          <label style={labelStyle}>
            <input
              type="radio"
              name={`print-format-${instanceId}`}
              value="docx"
              checked={format === "docx"}
              onChange={() => setFormat("docx")}
              style={{ marginRight: 6 }}
            />
            Word (.docx)
          </label>
        </div>
      </div>

      {/* ============================================================
         SECTION 3 — Template
      ============================================================ */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Document Template</div>

        <div style={{ display: "flex", flexDirection: "row", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Template</div>

            <select
              style={selectStyle}
              value={selectedTemplate || ""}
              onChange={(e) => setSelectedTemplate(e.target.value || null)}
            >
              {normalizedTemplates.length === 0 && (
                <option value="">No template available</option>
              )}

              {normalizedTemplates.map((tpl) => (
                <option key={tpl.value} value={tpl.value}>
                  {tpl.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Preview</div>
            <div style={thumbBoxStyle}>{previewContent}</div>
          </div>
        </div>
      </div>

      {/* ============================================================
         SECTION 4 — Layout
      ============================================================ */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Layout</div>

        <div style={{ display: "flex", flexDirection: "row", gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Orientation</div>
            <select
              style={selectStyle}
              value={orientation}
              onChange={(e) => setOrientation(e.target.value)}
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Font Size</div>
            <select
              style={selectStyle}
              value={fontSize}
              onChange={(e) => setFontSize(e.target.value)}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
        </div>

        {format === "docx" && (
          <div style={rowStyle}>
            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={includeToc}
                onChange={(e) => setIncludeToc(e.target.checked)}
              />
              Include table of contents
            </label>
          </div>
        )}
      </div>

      {/* ============================================================
         SECTION 5 — Actions
      ============================================================ */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button type="button" style={buttonSecondaryStyle} onClick={handleReset}>
          Reset
        </button>

        <button
          type="button"
          style={{
            ...buttonPrimaryStyle,
            opacity: canGenerate ? 1 : 0.6,
            cursor: canGenerate ? "pointer" : "default",
          }}
          onClick={handleGenerate}
          disabled={!canGenerate}
        >
          Generate Document
        </button>
      </div>
    </div>
  );
};

export default PrintMenuStandard;